"use client";
import debounce from "lodash/debounce";
import React, { useEffect, useRef, useState } from "react";
import { ExpandableBlockProps } from "../types";
import { cn } from "@/lib/utils";
import useTranslations from "@/i18n/useTranslations";

export default function ExpandableBlock({
  heading = "",
  maxHeight = 325,
  blur = false,
  children,
  showMoreLabel,
  btnClassName,
  blurClassName,
  centered = true,
  contentClassName = "",
  className,
  onExpandChange,
}: ExpandableBlockProps) {
  const t = useTranslations("ExpandableBlock");
  const containerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [height, setHeight] = useState<number>(maxHeight);
  const [shouldShowMore, setShouldShowMore] = useState(false);

  useEffect(() => {
    function handleResize() {
      if (containerRef?.current !== null) {
        const contentHeight = Math.ceil(containerRef?.current?.offsetHeight);
        setHeight(contentHeight);
        setShouldShowMore(contentHeight > maxHeight);
      }
    }

    handleResize();
    const debouncedHandleResize = debounce(handleResize, 100);
    window.addEventListener("resize", debouncedHandleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, [maxHeight]);

  const handleToggleExpansion = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // Notify parent component of expansion state change
    if (onExpandChange) {
      onExpandChange(newExpandedState);
    }
  };

  const showMoreText = showMoreLabel || t("showMore");

  return (
    <div className={cn(`${centered ? "mx-auto" : ""} bg-inherit`, className)}>
      {heading && (
        <p className="text-xl mb-3 text-primary font-bold">{heading}</p>
      )}
      <div
        data-testid="expandable-block"
        className={`overflow-hidden transition-max-height duration-500 ease-in-out relative ${isExpanded ? "max-h-fit" : ""}`}
        style={!isExpanded ? { maxHeight: `${maxHeight}px` } : {}}
      >
        <div className={cn("h-100", contentClassName)} ref={containerRef}>
          {children}
        </div>
        {blur && shouldShowMore && !isExpanded && (
          <div
            className={cn(
              "absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none",
              blurClassName,
            )}
          ></div>
        )}
      </div>
      {shouldShowMore && (
        <button
          data-testid="expandable-block-trigger"
          onClick={handleToggleExpansion}
          className={`text-black-100 underline ${btnClassName}`}
        >
          {isExpanded ? t("showLess") : showMoreText}
        </button>
      )}
    </div>
  );
}
